"use client"

import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Plus, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { toast } from "sonner"
import type { Entity, MappingConfig } from "@/types/fbo-api"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"
import { EntityHelpCard } from "@/components/entity-management/entity-help-card"
import { ReferenceIdBuilder } from "@/components/entity-management/reference-id-builder"
import { useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

const CUSTOM_TYPE = "__custom__"

interface EntityManagementTabProps {
  config: MappingConfig
  onConfigChange: (config: MappingConfig) => void
}

export function EntityManagementTab({ config, onConfigChange }: EntityManagementTabProps) {
  const { t } = useTranslation()
  const { atsSystem } = useTenantAtsSystem()
  const { loadEntityTypes } = useExplorer()

  const [newEntityType, setNewEntityType] = useState("")
  const [newEntityName, setNewEntityName] = useState("")
  const [newEntityReferenceId, setNewEntityReferenceId] = useState("")
  const [hasUserEditedName, setHasUserEditedName] = useState(false)
  const [entityTypes, setEntityTypes] = useState<AtsEntityTypeInfo[]>([])
  const [useCustomType, setUseCustomType] = useState(false)
  // Remounts the type Select on form reset — a Radix Select given value=undefined
  // goes uncontrolled and keeps displaying its previous selection
  const [formResetKey, setFormResetKey] = useState(0)

  // Load the live entity type catalog; vendors without explorer support (501)
  // fall back to free-text entry
  useEffect(() => {
    if (!atsSystem) return
    loadEntityTypes(atsSystem)
      // Only detail-fetchable entities work as mapping entities — composite
      // fetches resolve each entity by ID, which nested resources don't support
      .then((types) => setEntityTypes(types.filter((t) => t.supportsDetailFetch)))
      .catch(() => setEntityTypes([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem])

  // Name auto-syncs from the type inside the handler (not an effect — a state-syncing
  // effect wiped typed names whenever the type state was empty)
  const handleEntityTypeChange = (value: string) => {
    const entityType = value === CUSTOM_TYPE ? "" : value
    setUseCustomType(value === CUSTOM_TYPE)
    setNewEntityType(entityType)

    if (!hasUserEditedName) {
      setNewEntityName(entityType)
    }
  }

  const handleEntityNameChange = (value: string) => {
    setNewEntityName(value)
    // Mark that user has manually edited the name
    setHasUserEditedName(true)
  }

  const handleAddEntity = () => {
    if (!newEntityType || !newEntityName) {
      toast.error(t("entities.toasts.errorTitle"), {
        description: t("entities.toasts.typeAndNameRequired"),
      })
      return
    }

    // Check for duplicate entity names
    const existingEntityNames = config.entities.map(entity => entity.name)
    if (existingEntityNames.includes(newEntityName)) {
      toast.error(t("entities.toasts.errorTitle"), {
        description: t("entities.toasts.duplicateName", { name: newEntityName }),
      })
      return
    }

    // For non-primary entities (not the first entity), referenceId is required
    const isFirstEntity = config.entities.length === 0
    if (!isFirstEntity && !newEntityReferenceId) {
      toast.error(t("entities.toasts.errorTitle"), {
        description: t("entities.toasts.referenceIdRequired"),
      })
      return
    }

    const newEntity: Entity = {
      entityType: newEntityType,
      name: newEntityName,
      fieldMappings: [],
    }

    // Only non-primary entities (not the first) get a reference ID
    if (!isFirstEntity) {
      newEntity.referenceId = newEntityReferenceId
    }

    const updatedConfig = JSON.parse(JSON.stringify(config))
    updatedConfig.entities.push(newEntity)
    onConfigChange(updatedConfig)
    setNewEntityType("")
    setNewEntityName("")
    setNewEntityReferenceId("")
    setHasUserEditedName(false)
    setUseCustomType(false)
    setFormResetKey((key) => key + 1)
    toast.success(t("entities.toasts.addedTitle"), {
      description: t("entities.toasts.added", { name: newEntityName }),
    })
  }

  /**
   * Other entities that depend on this one via their Reference ID ("{name}.{path}")
   */
  const getDependentEntities = (entity: Entity): Entity[] =>
    config.entities.filter((e) => e !== entity && e.referenceId?.startsWith(`${entity.name}.`))

  const getRemovalBlockReason = (entityIndex: number): string | null => {
    const entity = config.entities[entityIndex]

    if (entity.fieldMappings.length > 0) {
      return t("entities.blocked.hasMappings")
    }

    const dependents = getDependentEntities(entity)
    if (dependents.length > 0) {
      return t("entities.blocked.referencedBy", { names: dependents.map((e) => e.name).join(", ") })
    }

    // The first entity is the primary; removing it while related entities remain
    // would silently promote the next entity, whose Reference ID semantics differ
    if (entityIndex === 0 && config.entities.length > 1) {
      return t("entities.blocked.primary")
    }

    return null
  }

  const handleRemoveEntity = (entityIndex: number) => {
    const blockReason = getRemovalBlockReason(entityIndex)
    if (blockReason) {
      toast.error(t("entities.toasts.cannotRemoveTitle"), {
        description: blockReason,
      })
      return
    }

    const updatedConfig = JSON.parse(JSON.stringify(config))
    updatedConfig.entities.splice(entityIndex, 1)
    onConfigChange(updatedConfig)

    toast.success(t("entities.toasts.removedTitle"), {
      description: t("entities.toasts.removed"),
    })
  }

  const updateConfigField = (field: keyof MappingConfig, value: string) => {
    onConfigChange({ ...config, [field]: value })
  }

  return (
    <div className="space-y-6">
      <section>
        <h3 className="th-section-label mb-2">{t("entities.sections.configuration")}</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="atsEntityType">{t("entities.labels.atsEntityType")}</Label>
              <Input
                id="atsEntityType"
                value={config.atsEntityType}
                onChange={(e) => updateConfigField("atsEntityType", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="atsSource">{t("entities.labels.atsSource")}</Label>
              <Input
                id="atsSource"
                value={config.atsSource}
                onChange={(e) => updateConfigField("atsSource", e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="th-section-label mb-2">{t("entities.sections.entities")}</h3>
        <div className="space-y-6">
          {/* Entities List */}
          <div>
            <h3 className="text-lg font-medium mb-4">{t("entities.sections.current")}</h3>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/4">{t("entities.table.name")}</TableHead>
                    <TableHead className="w-1/4">{t("entities.table.type")}</TableHead>
                    <TableHead className="w-1/4">{t("entities.table.referenceId")}</TableHead>
                    <TableHead className="w-1/6">{t("entities.table.mappings")}</TableHead>
                    <TableHead className="w-1/12 text-right">{t("entities.table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {config.entities.map((entity: Entity, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {entity.name}
                        {index === 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {t("entities.table.primary")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{entity.entityType}</TableCell>
                      <TableCell>{entity.referenceId || "-"}</TableCell>
                      <TableCell>{entity.fieldMappings.length}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveEntity(index)}
                          disabled={getRemovalBlockReason(index) !== null}
                          title={getRemovalBlockReason(index) ?? t("entities.actions.removeEntity")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <Separator />

          {/* Add New Entity */}
          <div>
            <h3 className="text-lg font-medium mb-4">{t("entities.sections.addNew")}</h3>

            <EntityHelpCard />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end mt-4">
              <div className="space-y-2">
                <Label htmlFor="entityType">{t("entities.form.entityType")}</Label>
                {entityTypes.length > 0 && !useCustomType ? (
                  <Select key={formResetKey} value={newEntityType || undefined} onValueChange={handleEntityTypeChange}>
                    <SelectTrigger id="entityType">
                      <SelectValue placeholder={t("entities.form.entityTypePlaceholder", { system: atsSystem ?? t("entities.form.atsFallback") })} />
                    </SelectTrigger>
                    <SelectContent>
                      {entityTypes.map((type) => (
                        <SelectItem key={type.name} value={type.name}>
                          {type.displayName} ({type.name})
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_TYPE}>{t("entities.form.customType")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="entityType"
                    placeholder={t("entities.form.entityTypeCustomPlaceholder")}
                    value={newEntityType}
                    onChange={(e) => handleEntityTypeChange(e.target.value === CUSTOM_TYPE ? "" : e.target.value)}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="entityName">{t("entities.form.entityName")}</Label>
                <Input
                  id="entityName"
                  placeholder={t("entities.form.entityNamePlaceholder")}
                  value={newEntityName}
                  onChange={(e) => handleEntityNameChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="referenceId">{t("entities.form.referenceId")}</Label>
                {config.entities.length === 0 ? (
                  // The first entity is the primary entity — its ID is supplied per
                  // record at sync/pull time, so there is nothing to reference
                  <p className="text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/30">
                    {t("entities.form.referenceIdNotNeeded")}
                  </p>
                ) : (
                  <Input
                    id="referenceId"
                    placeholder={t("entities.form.referenceIdPlaceholder")}
                    value={newEntityReferenceId}
                    onChange={(e) => setNewEntityReferenceId(e.target.value)}
                  />
                )}
              </div>
              <Button onClick={handleAddEntity}>
                <Plus className="h-4 w-4 mr-2" />
                {t("entities.actions.addEntity")}
              </Button>
            </div>

            {config.entities.length > 0 && (
              <ReferenceIdBuilder
                entities={config.entities}
                onReferenceIdBuilt={setNewEntityReferenceId}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
